/**
 * ProjectRoute - Route-level wrapper for project pages
 *
 * Handles project loading at the route level so individual pages
 * don't need to manage their own loading states.
 *
 * Benefits:
 * - Deep linking works (waits for project to load before rendering)
 * - Consistent loading experience across all project pages
 * - Pages can assume project exists (cleaner code)
 * - Single place to handle project not found
 */

import { Box, Button, Text, VStack } from '@chakra-ui/react';
import { Navigate, useParams } from 'react-router-dom';

import { PageLoadingSpinner } from '~/components/ui';
import { useProject, useProjectStore, type WorkflowStep } from '~/hooks/useProjectStore';

interface ProjectRouteProps {
  children: React.ReactNode;
  /**
   * If specified, checks if project has required data for this workflow step.
   * Redirects to setup page if prerequisites not met.
   */
  requiredStep?: WorkflowStep;
}

/**
 * Wraps project pages to handle loading and access control.
 *
 * Usage in routes.tsx:
 * ```tsx
 * {
 *   path: `/projects/:id/setup`,
 *   element: (
 *     <ProjectRoute>
 *       <ProjectSetupPage />
 *     </ProjectRoute>
 *   ),
 * }
 * ```
 */
export const ProjectRoute: React.FC<ProjectRouteProps> = ({ children, requiredStep }) => {
  const { id: projectId } = useParams<{ id: string }>();

  // Trigger project load if not already in store
  const { project, isLoading, error } = useProject(projectId || null);

  // Also get hasRequiredData for step checking
  const hasRequiredData = useProjectStore((s) => s.hasRequiredData);

  // No project ID in URL - redirect to home
  if (!projectId) {
    return <Navigate to="/" replace />;
  }

  // Still loading - show spinner
  if (isLoading) {
    return <PageLoadingSpinner message="Loading project..." />;
  }

  // Error loading or project not found
  if (error || !project) {
    return (
      <Box
        w="100%"
        h="calc(100vh - 73px)"
        display="flex"
        justifyContent="center"
        alignItems="center"
        bg="neutral.background"
      >
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.600">
            {error || 'Project not found'}
          </Text>
          <Button
            onClick={() => {
              window.location.href = '/';
            }}
          >
            Back to Home
          </Button>
        </VStack>
      </Box>
    );
  }

  // Check required step access (if specified)
  if (requiredStep) {
    const hasData = hasRequiredData(projectId, requiredStep);

    if (!hasData) {
      console.log('[ProjectRoute] Missing required data for step:', requiredStep, {
        projectId,
        dateInputs: !!project.dateInputs,
        taxValueInputs: !!project.taxValueInputs,
        millageInputs: !!project.millageInputs,
        drfInputs: !!project.drfInputs,
      });

      // Redirect to setup page
      return <Navigate to={`/projects/${projectId}/setup`} replace />;
    }
  }

  // All good - render the page
  return <>{children}</>;
};
